import React, { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Save, X } from 'lucide-react';
import AdminPageShell from '../components/AdminPageShell';
import API from '../../../util/API';

const cx = (...arr) => arr.filter(Boolean).join(' ');

const Card = ({ children, className }) => (
  <div className={cx('bg-white border border-slate-200 rounded-2xl', className)}>{children}</div>
);

const FieldBase =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30';

const SelectBase =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30';

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      'inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors disabled:opacity-60 disabled:cursor-not-allowed',
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      'inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed',
      className
    )}
  />
);

const CreateAdmin = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    designation: '',
    profilePic: '',
    department: '',
    roleId: '',
    password: '',
    confirmPassword: '',
  });

  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [serverOk, setServerOk] = useState('');

  const departments = [
    'Administration',
    'Product Management',
    'Order Management',
    'Customer Support',
    'Marketing',
    'Finance',
    'IT',
  ];

  useEffect(() => {
    const loadRoles = async () => {
      setLoadingRoles(true);
      try {
        const res = await API.get('/role');
        setRoles(res?.data?.data || []);
      } catch (e) {
        setRoles([]);
      } finally {
        setLoadingRoles(false);
      }
    };

    loadRoles();
  }, []);

  const username = useMemo(() => {
    const first = String(formData.firstName || '').trim();
    const last = String(formData.lastName || '').trim();
    const combined = [first, last].filter(Boolean).join(' ');
    if (combined) return combined;
    return String(formData.email || '').split('@')[0] || 'admin';
  }, [formData.firstName, formData.lastName, formData.email]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!String(formData.firstName || '').trim()) nextErrors.firstName = 'First name is required';
    if (!String(formData.lastName || '').trim()) nextErrors.lastName = 'Last name is required';
    if (!String(formData.email || '').trim()) nextErrors.email = 'Email is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = 'Enter a valid email address';
    }

    if (!formData.password) nextErrors.password = 'Password is required';
    if (formData.password && formData.password.length < 8) {
      nextErrors.password = 'Password must be at least 8 characters';
    }
    if (!formData.confirmPassword) nextErrors.confirmPassword = 'Confirm password is required';
    if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    setServerError('');
    setServerOk('');

    try {
      await API.post('/admin/create', {
        username,
        email: formData.email,
        phone: formData.phone,
        designation: formData.designation,
        profilePic: formData.profilePic,
        password: formData.password,
        roleId: formData.roleId || null,
      });

      setServerOk('Admin created successfully');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        designation: '',
        profilePic: '',
        department: '',
        roleId: '',
        password: '',
        confirmPassword: '',
      });
      setErrors({});
    } catch (e2) {
      setServerError(e2?.response?.data?.message || 'Failed to create admin');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminPageShell
      title="Create Admin"
      description="Add a new administrator to the system and assign a role."
      actions={
        <SecondaryButton type="button" onClick={() => window.history.back()}>
          <X size={16} /> Cancel
        </SecondaryButton>
      }
    >
      <Card className="p-5 md:p-6">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div>
            <div className="flex items-center gap-2">
              <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
              <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Basic Information</h2>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={cx(FieldBase, errors.firstName ? 'border-rose-300' : '')}
                  placeholder="Enter first name"
                />
                {errors.firstName ? <p className="text-rose-700 text-sm mt-1">{errors.firstName}</p> : null}
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={cx(FieldBase, errors.lastName ? 'border-rose-300' : '')}
                  placeholder="Enter last name"
                />
                {errors.lastName ? <p className="text-rose-700 text-sm mt-1">{errors.lastName}</p> : null}
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={cx(FieldBase, errors.email ? 'border-rose-300' : '')}
                  placeholder="Enter email address"
                />
                {errors.email ? <p className="text-rose-700 text-sm mt-1">{errors.email}</p> : null}
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  Phone
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={FieldBase}
                  placeholder="Enter phone"
                />
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  Designation
                </label>
                <input
                  type="text"
                  value={formData.designation}
                  onChange={(e) => handleInputChange('designation', e.target.value)}
                  className={FieldBase}
                  placeholder="Enter designation"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  Profile Picture URL
                </label>
                <input
                  type="text"
                  value={formData.profilePic}
                  onChange={(e) => handleInputChange('profilePic', e.target.value)}
                  className={FieldBase}
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>

          {/* Role and Department */}
          <div>
            <div className="flex items-center gap-2">
              <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
              <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Role & Department</h2>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Role</label>
                <select
                  value={formData.roleId}
                  onChange={(e) => handleInputChange('roleId', e.target.value)}
                  className={SelectBase}
                >
                  <option value="">(Optional) Select a role</option>
                  {roles.map((role) => (
                    <option key={role._id} value={role._id}>
                      {role.name}{role.description ? ` - ${role.description}` : ''}
                    </option>
                  ))}
                </select>
                {loadingRoles ? <p className="text-slate-500 text-xs mt-2">Loading roles…</p> : null}
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  Department
                </label>
                <select
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  className={SelectBase}
                >
                  <option value="">Select a department</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center gap-2">
              <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
              <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Security</h2>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={cx(FieldBase, 'pr-10', errors.password ? 'border-rose-300' : '')}
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password ? <p className="text-rose-700 text-sm mt-1">{errors.password}</p> : null}
                <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                  Confirm Password *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={cx(FieldBase, 'pr-10', errors.confirmPassword ? 'border-rose-300' : '')}
                    placeholder="Confirm password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.confirmPassword ? (
                  <p className="text-rose-700 text-sm mt-1">{errors.confirmPassword}</p>
                ) : null}
              </div>
            </div>
          </div>

          {serverError ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
              {serverError}
            </div>
          ) : null}
          {serverOk ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              {serverOk}
            </div>
          ) : null}

          {/* Form Actions */}
          <div className="flex flex-wrap gap-3 pt-6 border-t border-slate-200">
            <PrimaryButton type="submit" disabled={submitting}>
              <Save size={16} /> {submitting ? 'Creating…' : 'Create Admin'}
            </PrimaryButton>
            <SecondaryButton type="button" onClick={() => window.history.back()} disabled={submitting}>
              <X size={16} /> Cancel
            </SecondaryButton>
          </div>
        </form>
      </Card>
    </AdminPageShell>
  );
};

export default CreateAdmin;
